/** @license
 *
 * Copyright 2018 Esri
 *
 * All rights reserved under the copyright laws of the United States and applicable international laws, treaties,
 * and conventions.
 *
 * This material is licensed for use under the Esri Master License Agreement (MLA), and is bound by the terms of
 * that agreement. You may redistribute and use this code without modification, provided you adhere to the terms
 * of the MLA and include this copyright notice.
 *
 * See use restrictions at http://www.esri.com/legal/pdfs/mla_e204_e300/english
 *
 * For additional information, contact: Environmental Systems Research Institute, Inc. Attn: Contracts and Legal
 * Services Department 380 New York Street Redlands, California, USA 92373 USA
 *
 * email: contracts@esri.com
 */

/**
 * Launches the app.
 *
 * @module launcher
 */

import * as bridge from "./Bridge";
import * as common from "@esri/solution-common";
import * as intl from "@arcgis/core/intl";
import globalNavDefaultConfig from "./config/globalNavDefault.json";
import spatialReferences from "./config/spatialReferences.json";
import { Auth } from "./Auth";
import { decideAndSetLocale, decideAndSetDir } from "./t9nUtils";
import { GlobalNav } from "./GlobalNav";
import { Logger, LogLevel } from "./Logger";

export interface IAppConfigFile {
  appId: string;
  portalURL: string;
  primarySolutionsGroupId: string;
  agoBasedEnterpriseSolutionsGroupId: string;

  consoleMessageLevel: number;
  cookiesFirstUseVar: string;
  isRTL: boolean;
  locale: string;
  measurementIds: string[];
  suppress_solutionDiagramFeature_announcement?: boolean;
  trackingEnabled: boolean;
  forceTrackingEnv: bridge.TrackingEnv;
  telemetry: bridge.TelemetryOptions;
}

/**
 * Launches the app.
 *
 * @param appConfigFile Configuration from the app's appConfig.json file
 */
export function launch(
  appConfigFile: IAppConfigFile
): void {
  // Get UI customizations from URL search params
  const urlParams = new URLSearchParams(window.location.search);
  const inGalleryMode = urlParams.has("gallery");
  const localeInUrl = urlParams.has("locale");
  const inTestingMode = urlParams.has("testing");
  const logger = Logger.getInstance(appConfigFile.consoleMessageLevel);

  // If we're running in gallery mode, set a `gallery` attribute on `body` to drive some of our CSS
  // and change our browser tab icon to match the gallery's fellow pages Overview and Resources
  if (inGalleryMode) {
    document.querySelector("body")?.setAttribute("gallery", "true");
    (document.querySelector("link[rel~='icon']") as HTMLLinkElement).href = "./assets/images/esri-favicon-light-32.png";
  }

  // Likewise, if we're running in the MacOS, set a `macos` attribute on `body` to drive some of our CSS
  if (/mac os x/i.test(navigator.userAgent)) {
    document.querySelector("body")?.setAttribute("macos", "true");
    logger.log(LogLevel.Info, "Mac OS X");
  }

  // What's our sign-in status?
  const auth = new Auth(appConfigFile.appId, appConfigFile.portalURL, appConfigFile.locale, logger);
  auth.init()
  .then(() => {
    // Are we aiming to deploy immediately?
    const deploying = urlParams.has("deploy");

    // If we wanted to deploy and had attempted to sign in, but did not succeed
    // Triggers page refresh
    if (deploying && urlParams.has("signInParam") && !auth.SignedIn) {
      // Change the deploy parameter back to a solution viewing parameter and reload the page
      const solutionId = urlParams.get("deploy");
      if (solutionId) {
        urlParams.append("solution", solutionId);
      }
      urlParams.delete("deploy");
      urlParams.delete("signInParam");
      window.location.replace(window.location.origin + window.location.pathname + "?" + urlParams.toString());
      return;
    }

    if (auth.SignedIn || (inGalleryMode && (auth.SignedIn || !deploying)) || inTestingMode) {
      // If the initial locale doesn't match the user's AGO culture, reset the locale
      if (!localeInUrl && auth.PortalUserCulture &&
        !areLocalesCompatible(appConfigFile.locale, auth.PortalUserCulture)) {
        // Change the user locale
        appConfigFile.locale = decideAndSetLocale(window?.location?.search, auth.Portal, window?.navigator?.language);
        appConfigFile.isRTL = decideAndSetDir(appConfigFile.locale);
      }

      // Load our locale
      intl.fetchMessageBundle("deployment/strings")
      .then((bundle) => {

        // If we're signed in, set a signed-in attribute on `body` to drive some of our CSS
        if (auth.SignedIn) {
          document.querySelector("body")?.setAttribute("signed-in", "true");
        }

        // Make the page visible, which we've started out hidden so that it doesn't flash in the previous language
        const appContainer = document.getElementById("appContainer");
        if (appContainer) {
          appContainer.style.opacity = "1";
        }

        // Load the locale
        const pageTranslations = [
          bundle.header.menus[0][0].label,
          bundle.header.menus[0][1].label,
          bundle.header.subtitle
        ];

        // Save info that locale was overridden on URL
        const overrideLocale = localeInUrl ? appConfigFile.locale : "";

        const appConfig = {
          ...appConfigFile,
          pageTranslations,
          typescriptTranslations: bundle.typescript,
          elmTranslations: bundle.elm,
          dateNumFormatter: (value: number | Date) => intl.formatDate(value),
          overrideLocale,
          inGalleryMode: urlParams.has("gallery"),
          inTestingMode: urlParams.has("testing"),
          logger
        } as bridge.IAppConfig;

        // Initialize the Esri header & footer
        const globalNavMenus = new GlobalNav(appConfigFile.locale);
        void globalNavMenus.build(!appConfig.inGalleryMode, globalNavDefaultConfig, bundle, auth)
        .then(
          () => {
            // If we're running in AGO, then we are not using the `agoBasedEnterpriseSolutionsGroupId` config property
            // to look for supplemental Enterprise Solutions hosted in AGO
            if (!auth.IsEnterprisePortal) {
              appConfig.agoBasedEnterpriseSolutionsGroupId = "";
              return Promise.resolve({});
            } else {
              // Query the Enterprise portal settings to get the portal config property `searchArcGISOnlineEnabled`
              return common.getJson(appConfig.portalURL + "/sharing/rest/portals/self/settings");
            }
          }
        )
        .then(
          (settingsInfo: common.IStringValuePair) => {
            // If we're running in Enterprise, we need to check the portal config property `searchArcGISOnlineEnabled`.
            // If it exists and it's false, then we ignore the app config parameter `agoBasedEnterpriseSolutionsGroupId`
            // because the property tells us to not look in AGO for Solutions. If the property is not found, then we're
            // in an Enterprise version before 11 and we'll support looking in AGO.
            if (
              auth.IsEnterprisePortal &&
              !common.getPropWithDefault(settingsInfo, "portalConfigProperties.searchArcGISOnlineEnabled", true)
            ) {
              appConfig.agoBasedEnterpriseSolutionsGroupId = "";
            }

            logger.log(LogLevel.Info, "Running in " + (auth.IsEnterprisePortal ? "Enterprise" : "AGO"));
            logger.log(LogLevel.Info, "Searching for Solutions");
            if (appConfig.primarySolutionsGroupId) {
              logger.log(LogLevel.Info, auth.IsEnterprisePortal ? "    in primary group" : "    in AGO-based Solutions group");
            }
            if (appConfig.agoBasedEnterpriseSolutionsGroupId) {
              logger.log(LogLevel.Info, "    in AGO-based Solutions group ");
            }

            // Initialize the online solution UI
            new bridge.Bridge(appConfig, document.getElementById("uiContainer")!, auth, spatialReferences);
          }
        );
      })
      .catch(error => {
        logger.log(LogLevel.Errors, "Unable to launch app: " + error);
      });
    }
    else {
      // Add a signIn parameter if we're deploying so that we can tell if sign-in fails or is canceled
      auth.signIn(deploying ? "deploySignIn" : "");
    }
  })
  .catch(error => {
    logger.log(LogLevel.Errors, "Unable to sign in: " + error);
  });
}

//--------------------------------------------------------------------------------------------------------------------//

/**
 * Tests if two locales are either the same or one is a subset of the other: that their languages are the same even if
 * one locale is missing a region
 *
 * @param locale1 Locale to test
 * @param locale2 Locale to test
 * @return Locales are compatible, e.g., "en" & "en", or "en-us" & "en", or "en" & "en-gb"; "en-us" & "en-gb"
 *         are not considered compatible
 */
function areLocalesCompatible(
  locale1: string,
  locale2: string
): boolean {
  return locale1 === locale2 || locale1 === getLocaleLang(locale2) || getLocaleLang(locale1) === locale2;
}

/**
 * Returns the first part of a locale, e.g., "en" for "en-us" or "en".
 *
 * @param locale Locale to split
 * @return First part of locale
 */
function getLocaleLang(
  locale: string
): string {
  return locale.split("-")[0];
}
